/**
 * Sysadmin Tenant Detail — Branding tab (Wave 3 §10.2 #2 / spec §4.7).
 *
 * Three sections:
 *   1. Effective preview — what the tenant currently sees
 *   2. Override editor — sysadmin overwrites the tenant's branding row
 *      (NOT subject to lock list, since sysadmin sets the locks)
 *   3. Lock toggles — per-field switches that enforce the tenant cannot
 *      override
 *
 * Migrated verbatim from the prior `useState`-tab implementation; only
 * the route entry changed.
 */

import { useState } from 'react';
import { createFileRoute, useParams } from '@tanstack/react-router';
import { AlertTriangle } from 'lucide-react';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Button } from '@/components/ui/button';
import {
  useSysadminTenantBranding,
  useUpdateSysadminTenantBranding,
  useUpdateTenantBrandingLocks,
} from '@/lib/use-platform-branding';
import { toast } from 'sonner';

export const Route = createFileRoute('/sysadmin/tenants/$tenantId/branding')({
  component: TenantBrandingTab,
});

const LOCKABLE_FIELDS: Array<{ key: string; label: string }> = [
  { key: 'portalTitle',           label: 'Portal title' },
  { key: 'displayMode',           label: 'Display mode' },
  { key: 'footerText',            label: 'Footer text' },
  { key: 'logoFullAssetId',       label: 'Full logo' },
  { key: 'logoIconAssetId',       label: 'Icon logo' },
  { key: 'faviconAssetId',        label: 'Favicon' },
  { key: 'loginBackgroundAssetId', label: 'Login background' },
  { key: 'loginWelcomeText',      label: 'Login welcome text' },
  { key: 'loginSubtitle',         label: 'Login subtitle' },
  { key: 'light.primary',         label: 'Light: primary' },
  { key: 'light.background',      label: 'Light: background' },
  { key: 'dark.primary',          label: 'Dark: primary' },
  { key: 'dark.background',       label: 'Dark: background' },
];

function TenantBrandingTab() {
  const { tenantId } = useParams({ from: '/sysadmin/tenants/$tenantId/branding' });
  const { data, isLoading, error } = useSysadminTenantBranding(tenantId);
  const updateOverride = useUpdateSysadminTenantBranding(tenantId);
  const updateLocks = useUpdateTenantBrandingLocks(tenantId);

  const [draft, setDraft] = useState<{
    portalTitle?: string | null;
    footerText?: string | null;
    displayMode?: 'full' | 'both' | 'icon-name';
    light?: Record<string, string>;
    dark?: Record<string, string>;
  }>({});
  const [lockDraft, setLockDraft] = useState<string[] | null>(null);

  if (isLoading) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-muted-foreground">
          Loading…
        </CardContent>
      </Card>
    );
  }

  if (error || !data) {
    return (
      <Card>
        <CardContent className="py-10 text-center">
          <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto mb-3" />
          <h3 className="font-semibold">Failed to load branding</h3>
          <p className="text-sm text-muted-foreground mt-1">
            {error?.message ?? 'Unknown error'}
          </p>
        </CardContent>
      </Card>
    );
  }

  const { effective, tenantOverride, platformDefault, lockedFields } = data;
  const currentLocks = lockDraft ?? lockedFields;
  const lockSet = new Set(currentLocks);
  const isOverrideDirty = Object.keys(draft).length > 0;
  const isLocksDirty =
    lockDraft !== null &&
    JSON.stringify([...currentLocks].sort()) !==
      JSON.stringify([...lockedFields].sort());

  const onSaveOverride = async () => {
    try {
      const body: Record<string, unknown> = {};
      if (draft.portalTitle !== undefined) body.portalTitle = draft.portalTitle;
      if (draft.footerText !== undefined) body.footerText = draft.footerText;
      if (draft.displayMode !== undefined) body.displayMode = draft.displayMode;
      if (draft.light !== undefined) body.light = draft.light;
      if (draft.dark !== undefined) body.dark = draft.dark;
      await updateOverride.mutateAsync(body);
      setDraft({});
      toast.success('Sysadmin override saved');
    } catch (err) {
      toast.error('Override save failed', { description: (err as Error).message });
    }
  };

  const onSaveLocks = async () => {
    try {
      await updateLocks.mutateAsync({ lockedFields: [...currentLocks] });
      setLockDraft(null);
      toast.success('Lock list updated');
    } catch (err) {
      toast.error('Lock update failed', { description: (err as Error).message });
    }
  };

  const toggleLock = (field: string) => {
    const set = new Set(currentLocks);
    if (set.has(field)) set.delete(field);
    else set.add(field);
    setLockDraft([...set]);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Effective branding</CardTitle>
          <CardDescription>
            What this tenant currently renders (override → platform
            default → system default).
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <Row label="Portal title" value={effective.portalTitle} />
          <Row label="Footer text" value={effective.footerText ?? '—'} />
          <Row label="Display mode" value={effective.displayMode} />
          <Row label="Source" value={effective.source} mono />
          <Row label="Effective version" value={String(effective.effectiveVersion)} mono />
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Sysadmin override</CardTitle>
          <CardDescription>
            Direct write to this tenant's organization_branding row.
            Bypasses the per-field lock list (the sysadmin owns the
            locks).
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="ovr-portalTitle">Portal title</Label>
            <Input
              id="ovr-portalTitle"
              value={
                draft.portalTitle ??
                tenantOverride?.portalTitle ??
                effective.portalTitle ??
                ''
              }
              onChange={(e) =>
                setDraft((d) => ({ ...d, portalTitle: e.target.value || null }))
              }
              data-testid="sysadmin-override-portal-title"
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="ovr-footerText">Footer text</Label>
            <Input
              id="ovr-footerText"
              value={
                draft.footerText ??
                tenantOverride?.footerText ??
                effective.footerText ??
                ''
              }
              onChange={(e) =>
                setDraft((d) => ({ ...d, footerText: e.target.value || null }))
              }
            />
          </div>
          <div className="flex justify-end gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setDraft({})}
              disabled={!isOverrideDirty || updateOverride.isPending}
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={onSaveOverride}
              disabled={!isOverrideDirty || updateOverride.isPending}
              data-testid="sysadmin-override-save"
            >
              Save override
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Locked fields</CardTitle>
          <CardDescription>
            Locked fields cannot be modified by the tenant admin. Toggle
            a switch and click Save to apply.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            {LOCKABLE_FIELDS.map((f) => (
              <label
                key={f.key}
                className="flex items-center justify-between rounded border p-2 text-sm"
                data-testid={`lock-${f.key}`}
              >
                <span>{f.label}</span>
                <Switch
                  checked={lockSet.has(f.key)}
                  onCheckedChange={() => toggleLock(f.key)}
                />
              </label>
            ))}
          </div>
          <div className="flex justify-end gap-2 mt-4">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setLockDraft(null)}
              disabled={!isLocksDirty || updateLocks.isPending}
            >
              Reset
            </Button>
            <Button
              size="sm"
              onClick={onSaveLocks}
              disabled={!isLocksDirty || updateLocks.isPending}
              data-testid="sysadmin-locks-save"
            >
              Save locks
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Platform default snapshot</CardTitle>
          <CardDescription>
            The defaults that feed this tenant when the override is
            empty. Edit at <code>/sysadmin/branding</code>.
          </CardDescription>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <Row label="Portal title" value={platformDefault.portalTitle ?? '—'} />
          <Row label="Footer text" value={platformDefault.footerText ?? '—'} />
          <Row label="Display mode" value={platformDefault.displayMode} />
          <Row
            label="Effective version"
            value={String(platformDefault.effectiveVersion)}
            mono
          />
        </CardContent>
      </Card>
    </div>
  );
}

function Row({
  label,
  value,
  mono = false,
}: {
  label: string;
  value: string;
  mono?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3">
      <span className="text-muted-foreground w-40">{label}</span>
      <span className={mono ? 'font-mono text-xs' : ''}>{value}</span>
    </div>
  );
}
